import React, { useEffect, useState } from 'react';
import { getDocs, query, collection, where } from 'firebase/firestore';
import { db } from '../firebase/config';
import {
    Container,
    Paper,
    TextField,
    Button,
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
} from '@mui/material';
import { Delete as DeleteIcon, Add as AddIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useAuth } from '../hooks/useAuth';
import { updateDocument, addDocument, deleteDocument } from '../firebase/firestore';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';

const profileSchema = yup.object({
    companyName: yup.string().required('Company name is required'),
    description: yup.string().required('Description is required'),
    industry: yup.string().required('Industry is required'),
    website: yup.string().url('Invalid URL'),
});

const teamMemberSchema = yup.object({
    name: yup.string().required('Name is required'),
    position: yup.string().required('Position is required'),
    email: yup.string().email('Invalid email'),
    phone: yup.string(),
    yearsOfExperience: yup.number().min(0),
});

export default function StartupProfile() {
    const { user, userProfile, loading: authLoading } = useAuth();
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [teamMembers, setTeamMembers] = useState([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const profileForm = useForm({
        resolver: yupResolver(profileSchema),
        defaultValues: {
            companyName: userProfile?.companyName || '',
            description: userProfile?.description || '',
            industry: userProfile?.industry || '',
            website: userProfile?.website || '',
        },
    });

    const teamForm = useForm({
        resolver: yupResolver(teamMemberSchema),
        defaultValues: {
            name: '',
            position: '',
            email: '',
            phone: '',
            yearsOfExperience: 0,
        },
    });

    useEffect(() => {
        const fetchTeamMembers = async () => {
            if (!user?.uid) return;
            try {
                const members = await getDocs(
                    query(collection(db, 'teams'), where('userId', '==', user.uid))
                );
                setTeamMembers(members.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
            } catch (err) {
                console.error('Error fetching team members:', err);
            }
        };

        if (!authLoading) {
            fetchTeamMembers();
        }
    }, [user, authLoading]);

    const handleProfileSubmit = async (data) => {
        try {
            setLoading(true);
            setError('');
            setSuccess('');

            await updateDocument('users', user.uid, data);
            setSuccess('Profile updated successfully!');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleAddTeamMember = async (data) => {
        try {
            setLoading(true);
            setError('');

            await addDocument('teams', {
                ...data,
                userId: user.uid,
                createdAt: new Date(),
            });

            teamForm.reset();
            setOpenDialog(false);
            setSuccess('Team member added successfully!');

            // Refresh team members
            const members = await getDocs(
                query(collection(db, 'teams'), where('userId', '==', user.uid))
            );
            setTeamMembers(members.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteTeamMember = async (memberId) => {
        if (!window.confirm('Are you sure you want to delete this team member?')) return;

        try {
            setLoading(true);
            await deleteDocument('teams', memberId);
            setTeamMembers((prev) => prev.filter((m) => m.id !== memberId));
            setSuccess('Team member deleted successfully!');
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    if (authLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
            <Sidebar open={sidebarOpen} onToggle={() => setSidebarOpen(!sidebarOpen)} />

            <Box sx={{ flex: 1 }}>
                <Navbar title="Startup Profile" onMenuClick={() => setSidebarOpen(!sidebarOpen)} />

                <Container maxWidth="lg" sx={{ py: 4 }}>
                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                    {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                    {/* Profile Form */}
                    <Paper sx={{ p: 3, mb: 4 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                            Company Information
                        </Typography>

                        <form onSubmit={profileForm.handleSubmit(handleProfileSubmit)}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="companyName"
                                        control={profileForm.control}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Company Name"
                                                error={!!error}
                                                helperText={error?.message}
                                                disabled={loading}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Controller
                                        name="industry"
                                        control={profileForm.control}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Industry"
                                                error={!!error}
                                                helperText={error?.message}
                                                disabled={loading}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Controller
                                        name="description"
                                        control={profileForm.control}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Description"
                                                multiline
                                                rows={4}
                                                error={!!error}
                                                helperText={error?.message}
                                                disabled={loading}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Controller
                                        name="website"
                                        control={profileForm.control}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Website (optional)"
                                                error={!!error}
                                                helperText={error?.message}
                                                disabled={loading}
                                            />
                                        )}
                                    />
                                </Grid>

                                <Grid item xs={12}>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={loading}
                                    >
                                        Save Profile
                                    </Button>
                                </Grid>
                            </Grid>
                        </form>
                    </Paper>

                    {/* Team Members */}
                    <Paper sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                Team Members ({teamMembers.length})
                            </Typography>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setOpenDialog(true)}
                            >
                                Add Member
                            </Button>
                        </Box>

                        <Grid container spacing={2}>
                            {teamMembers.map((member) => (
                                <Grid item xs={12} md={6} key={member.id}>
                                    <Card>
                                        <CardContent>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                {member.name}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {member.position}
                                            </Typography>
                                            <Typography variant="caption">{member.email}</Typography>
                                        </CardContent>
                                        <CardActions>
                                            <Button
                                                size="small"
                                                color="error"
                                                startIcon={<DeleteIcon />}
                                                onClick={() => handleDeleteTeamMember(member.id)}
                                            >
                                                Delete
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            ))}
                        </Grid>
                    </Paper>

                    {/* Add Team Member Dialog */}
                    <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                        <DialogTitle>Add Team Member</DialogTitle>
                        <DialogContent>
                            <Box sx={{ pt: 2 }}>
                                <form onSubmit={teamForm.handleSubmit(handleAddTeamMember)}>
                                    <Controller
                                        name="name"
                                        control={teamForm.control}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Name"
                                                error={!!error}
                                                helperText={error?.message}
                                                margin="normal"
                                            />
                                        )}
                                    />

                                    <Controller
                                        name="position"
                                        control={teamForm.control}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Position"
                                                error={!!error}
                                                helperText={error?.message}
                                                margin="normal"
                                            />
                                        )}
                                    />

                                    <Controller
                                        name="email"
                                        control={teamForm.control}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Email"
                                                error={!!error}
                                                helperText={error?.message}
                                                margin="normal"
                                            />
                                        )}
                                    />

                                    <Controller
                                        name="phone"
                                        control={teamForm.control}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Phone"
                                                error={!!error}
                                                helperText={error?.message}
                                                margin="normal"
                                            />
                                        )}
                                    />

                                    <Controller
                                        name="yearsOfExperience"
                                        control={teamForm.control}
                                        render={({ field, fieldState: { error } }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                label="Years of Experience"
                                                type="number"
                                                error={!!error}
                                                helperText={error?.message}
                                                margin="normal"
                                            />
                                        )}
                                    />

                                    <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                                        <Button
                                            type="submit"
                                            variant="contained"
                                            disabled={loading}
                                        >
                                            Add
                                        </Button>
                                        <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                                    </Box>
                                </form>
                            </Box>
                        </DialogContent>
                    </Dialog>
                </Container>
            </Box>
        </Box>
    );
}
